import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Zap,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Code,
  Share2,
  Copy
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  dynamicAnalysisApi
} from '../../services/dynamic-analysis-api';
import { DynamicAnalysisResult } from '../../components/dynamic-analysis';
import { formatDuration } from '../../components/dynamic-analysis/format';
import { useTimezone } from '../../contexts/TimezoneContext';
import toast from 'react-hot-toast';

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

const ResultDetailPage: React.FC = () => {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();
  const { formatRelativeTime } = useTimezone();
  const [showMetadata, setShowMetadata] = useState(false);
  const [showMetaAnalysis, setShowMetaAnalysis] = useState(false);

  // Fetch result details
  const { data: resultData, isLoading } = useQuery({
    queryKey: ['dynamic-analysis-result', resultId],
    queryFn: () => dynamicAnalysisApi.getResultById(resultId!),
    enabled: !!resultId
  });

  const result = resultData?.result;


  const getFilterLabel = () => {
    const filter = result?.entry_info?.filter;
    if (!filter || !filter.filters || filter.filters.length === 0) {
      return 'Baseline (no filter)';
    }
    const f = filter.filters[0];
    return `${f.field} = "${f.value}"`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Result not found</p>
        <Button onClick={() => navigate('/dynamic-analysis')} className="mt-4">
          Back to Jobs
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (result.analysis_jobs_dynamic_id) {
              navigate(`/dynamic-analysis/jobs/${result.analysis_jobs_dynamic_id}`);
            } else {
              navigate('/dynamic-analysis');
            }
          }}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-warn" />
            <h1 className="text-2xl font-bold text-foreground">
              Analysis Result
            </h1>
            <Badge 
              variant={result.significance_status ? 'success' : result.error_message ? 'error' : 'warning'}
            >
              {result.significance_status ? 'Significant' : result.error_message ? 'Error' : 'Insufficient Data'}
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            {getFilterLabel()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCopyLink}>
            <Copy className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'Analysis Result',
                  url: window.location.href
                });
              } else {
                handleCopyLink();
              }
            }}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>
      </div>

      {/* Result Info Card */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="flex items-center gap-3">
            {result.significance_status ? (
              <CheckCircle className="w-5 h-5 text-success" />
            ) : result.error_message ? (
              <XCircle className="w-5 h-5 text-destructive" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-warn" />
            )}
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="font-medium text-foreground">
                {result.significance_status ? 'Significant' : result.error_message ? 'Error' : 'Insufficient Data'}
              </p>
            </div>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Analysis Level</p>
            <p className="font-medium text-foreground">
              {result.entry_info?.analysisLevel || 'transactions'}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Execution Time</p>
            <p className="font-medium text-foreground">
              {result.execution_time_seconds != null ? `${Number(result.execution_time_seconds).toFixed(2)}s` : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Created</p>
            <p className="font-medium text-foreground">
              {formatRelativeTime(result.created_at)}
            </p>
          </div>
        </div>

        <div className="text-xs text-muted-foreground font-mono mb-4">
          Result ID: {result.id}
        </div>

        {result.entry_info?.metaAnalysis?.itemCodeName && (
          <p className="text-sm text-info">
            Item: {result.entry_info.metaAnalysis.itemCodeName}
          </p>
        )}
      </Card>

      {/* Result Content */}
      {result.result_data && (
        <Card>
          <div className="p-6">
            {result.significance_status ? (
              <>
                {/* Meta Analysis Context — Collapsible */}
                {result.entry_info?.metaAnalysis && (
                  <div className="mb-6">
                    <button
                      onClick={() => setShowMetaAnalysis(!showMetaAnalysis)}
                      className="flex items-center gap-2 text-sm font-medium text-foreground mb-3 hover:text-foreground transition-colors"
                    >
                      {showMetaAnalysis ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      Meta Analysis Context
                      <span className="text-xs font-normal text-muted-foreground">
                        {result.entry_info.metaAnalysis.dataFrequencyCategory || ''} frequency · {
                          result.entry_info.metaAnalysis.interArrivalTimeDistributionNormalizedMeanLast40Transactions != null
                            ? `~${formatDuration(result.entry_info.metaAnalysis.interArrivalTimeDistributionNormalizedMeanLast40Transactions)} cycle`
                            : ''
                        }
                      </span>
                    </button>
                    {showMetaAnalysis && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 bg-muted/50 p-4 rounded-lg">
                        <div>
                          <p className="text-xs text-muted-foreground">Data Frequency</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.dataFrequencyCategory || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Historical Context</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.historicalContextType?.replace(/_/g, ' ') || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Avg Entries (7d)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.dailyAvgEntriesLast7DaysExclNullDays?.toFixed(2) ?? 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Avg Entries (30d)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.dailyAvgEntriesLast30DaysExclNullDays?.toFixed(2) ?? 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Null Days (30d)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.numberOfNullDaysLast30Days ?? 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Mean IAT (recent)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.interArrivalTimeDistributionMeanLastXDays != null
                              ? formatDuration(result.entry_info.metaAnalysis.interArrivalTimeDistributionMeanLastXDays)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Mean IAT (40 txn)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.interArrivalTimeDistributionNormalizedMeanLast40Transactions != null
                              ? formatDuration(result.entry_info.metaAnalysis.interArrivalTimeDistributionNormalizedMeanLast40Transactions)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Std Dev IAT (40 txn)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.interArrivalTimeDistributionStdDevNormalizedLast40Transactions != null
                              ? formatDuration(result.entry_info.metaAnalysis.interArrivalTimeDistributionStdDevNormalizedLast40Transactions)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Weekly Cyclicality (F)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.weeklyCyclicalityMeasureAnovaFStatistic?.toFixed(2) ?? 'N/A'}
                            {result.entry_info.metaAnalysis.weeklyCyclicalityMeasureAnovaFStatistic != null && (
                              <span className="text-xs font-normal text-muted-foreground ml-1">
                                {result.entry_info.metaAnalysis.weeklyCyclicalityMeasureAnovaFStatistic > 3 ? '(cyclic)' : '(none)'}
                              </span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Daily Cyclicality (F)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.dailyCyclicalityMeasureAnovaFStatistic?.toFixed(2) ?? 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Avg Source Data Lag (Fixed Placeholder for now)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.avgSourceDataLag != null
                              ? formatDuration(result.entry_info.metaAnalysis.avgSourceDataLag)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Quantitative Fields</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.objectiveQuantitativeFields?.join(', ') || 'N/A'}
                          </p>
                        </div>
                        {result.entry_info.metaAnalysis.itemCodeName && (
                          <div>
                            <p className="text-xs text-muted-foreground">Item Name</p>
                            <p className="text-sm font-medium text-info">
                              {result.entry_info.metaAnalysis.itemCodeName}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <DynamicAnalysisResult resultData={result.result_data} meta={result.entry_info?.metaAnalysis} />

                {/* Raw Result Data Section */}
                <div className="mt-6 border-t border-border pt-4">
                  <button
                    onClick={() => setShowMetadata(!showMetadata)}
                    className={`
                      w-full flex items-center justify-between px-4 py-3 rounded-lg
                      transition-all duration-200
                      ${showMetadata 
                        ? 'bg-muted border border-input' 
                        : 'bg-muted/50 hover:bg-muted border border-border'}
                      cursor-pointer
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-background rounded-lg shadow-sm">
                        <Code className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="text-left">
                        <span className="font-medium text-foreground">
                          Raw Result Data
                        </span>
                        <p className="text-xs text-muted-foreground">
                          Full analysis result JSON
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {showMetadata ? (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  {showMetadata && (
                    <div className="mt-4 animate-in slide-in-from-top-2 duration-200">
                      <pre className="p-4 code-block rounded-lg text-xs overflow-x-auto max-h-96 overflow-y-auto font-mono">
                        {JSON.stringify(result.result_data, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </>
            ) : result.error_message ? (
              <div className="p-4 bg-destructive/15 rounded-lg">
                <p className="text-sm text-destructive">
                  <strong>Error:</strong> {result.error_message}
                </p>
              </div>
            ) : (
              <div className="p-4 bg-warn/15 rounded-lg">
                <p className="text-sm text-warn">
                  <strong>Error:</strong> {result.result_data?.reason || result.result_data?.error || 'Analysis failed.'}
                </p>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default ResultDetailPage;

